import { useLocation } from 'react-router-dom';
import { useWebLocale } from '../contexts/WebLocaleContext';
import { albumCorrectionMessage } from './albumCorrectionMessage';
import React, { useState, useEffect, useRef } from 'react';
import { Typography, Box, Grid, Divider, Stack } from '@mui/material';
import { contactApi } from '../api/contact';
import { aboutApi } from '../api/about';
import PageLayout from '../components/layout/PageLayout';
import countryList from '../data/countries.json';
import BasicButton from '../components/button/BasicButton';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import CustomTextField from '../components/common/CustomTextField';
import CustomTextareaAutosize from '../components/common/CustomTextareaAutosize';
import theme from '../theme';
import CustomSelectBox from '../components/common/CustomSelectBox';
import alertCompletedIcon from '../assets/alert_complete.svg';
import SEO from '../components/common/SEO';
import { Helmet } from 'react-helmet-async';
import { SITE_URL } from '../utils/env';
import { useUserStore } from '../stores/userStore';
import { IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ReactQuillEditor from '../components/Editor/ReactQuillEditor';
import ContentViewer from '../components/common/ContentViewer';
import CancelConfirmDialog from '../components/dialog/CancelConfirmDialog';
import ProgressBar from '../components/common/ProgressBar';
import { AccountBoundary, useRequestScope } from '../features/settings/shared';
import DraftGuard from '../features/contact/ContactDraftGuard';
import { translate } from '../features/contact/model';

const schema = yup.object().shape({
  name: yup.string().required('성함을 입력해 주세요.'),
  email: yup
    .string()
    .email('올바른 이메일 형식을 입력해 주세요.')
    .required('이메일을 입력해 주세요.')
    .matches(/@[^.]*\./, '올바른 이메일 형식을 입력해 주세요.'),
  telephone: yup.string(),
  location: yup.string(),
  message: yup.string().required('내용을 입력해 주세요.'),
});

const DEFAULT_CONTACT_DESC =
  '<p>협업, 광고 등의 문의는 아래 양식을 통해 보내주시면 검토 후 신속하게 회신드리겠습니다.</p><p>또한 서비스 이용 중 발생한 버그, 신고 및 건의사항도 아래 양식을 통해 접수해주세요.</p>';

const Contact = ({ user, validateSession }) => {
  const { state } = useLocation();
  const { locale } = useWebLocale();
  const isAdmin = useUserStore((state) => state.isAdmin);
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting, isDirty },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      email: '',
      telephone: '',
      location: '',
      message: albumCorrectionMessage(state, locale === 'en'),
    },
  });
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [descContent, setDescContent] = useState('');
  const [descLoading, setDescLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);

  const [loadError, setLoadError] = useState(false);
  const [reload, setReload] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [documentLocale, setDocumentLocale] = useState(locale);
  const selectedLocale = useRef(locale);
  selectedLocale.current = locale;
  const editing = useRef(isEditing);
  editing.current = isEditing;
  const request = useRequestScope(user);
  const requestRef = useRef(request);
  requestRef.current = request;
  const lock = useRef(false);
  const guard = useRef({});
  const editorDirty = isEditing && editContent !== descContent;
  guard.current = {
    released: success,
    dirty: !success && (isDirty || editorDirty),
    busy: saving || isSubmitting,
  };
  const draftGuard = (
    <DraftGuard state={guard} t={(key, values) => translate(locale, key, values)} />
  );
  const editTrigger = useRef(null);
  const focusEdit = () =>
    requestAnimationFrame(() =>
      editTrigger.current?.querySelector('button')?.focus({ preventScroll: true }),
    );
  useEffect(() => {
    if (editing.current) return undefined;
    const r = requestRef.current();
    setDescLoading(true);
    setLoadError(false);
    aboutApi
      .getContent('CONTACT', locale, r.options)
      .then(({ data }) => {
        if (r.current()) {
          setDescContent(data?.content || DEFAULT_CONTACT_DESC);
          setDocumentLocale(locale);
        }
      })
      .catch(() => {
        if (r.current()) setLoadError(true);
      })
      .finally(() => {
        if (r.mounted()) {
          setDescLoading(false);
          if (!r.current()) setLoadError(true);
        }
        r.finish();
      });
    return () => r.cancel();
  }, [locale, reload]);

  const onClickEdit = () => {
    if (lock.current || descLoading || loadError || !validateSession()) return;
    setEditContent(descContent);
    setSaveError('');
    setIsEditing(true);
  };

  const handleDescSubmit = async () => {
    if (lock.current || !isAdmin || !validateSession()) return;
    lock.current = true;
    setSaving(true);
    setSaveError('');
    const r = request();
    try {
      const { data } = await aboutApi.updateContent(
        editContent,
        'CONTACT',
        documentLocale,
        r.options,
      );
      if (!r.current()) return;
      setDescContent(data?.content ?? editContent);
      setIsEditing(false);
      if (documentLocale !== selectedLocale.current) setReload((value) => value + 1);
      focusEdit();
    } catch {
      if (r.current()) setSaveError('안내를 저장하지 못했습니다. 다시 시도해 주세요.');
    } finally {
      lock.current = false;
      if (r.mounted()) {
        setSaving(false);
        if (!r.current()) setSaveError('로그인 상태가 변경되었습니다. 다시 시도해 주세요.');
      }
      r.finish();
    }
  };

  const handleCancelConfirm = () => {
    if (lock.current) return;
    setIsCancelDialogOpen(false);
    setIsEditing(false);
    setEditContent(descContent);
    if (documentLocale !== selectedLocale.current) setReload((value) => value + 1);
    focusEdit();
  };

  const onSubmit = async (formData) => {
    if (success || lock.current || !validateSession()) return;
    lock.current = true;
    const r = request();
    setSubmitError('');
    try {
      await contactApi.sendEmail(formData, [], r.options);
      if (!r.current()) return;
      setSuccess(true);
      reset();
      window.scrollTo(0, 0);
    } catch {
      if (r.current()) setSubmitError('문의를 보내지 못했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      lock.current = false;
      if (r.mounted() && !r.current())
        setSubmitError('로그인 상태가 변경되었습니다. 다시 시도해 주세요.');
      r.finish();
    }
  };

  if (isEditing) {
    return (
      <PageLayout>
        {draftGuard}
        <Box
          sx={{
            maxWidth: '780px',
            minWidth: 0,
            width: '100%',
            mx: 'auto',
            '& .MuiFormControl-root': { minWidth: 0 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              mb: 2,
            }}
          >
            <Typography
              sx={{ fontWeight: 'bold', fontSize: { lg: '28px', md: '22px', xs: '20px' } }}
            >
              문의
            </Typography>
          </Box>
          {documentLocale !== locale && (
            <Typography role="status">
              {documentLocale === 'ko'
                ? '한국어 안내를 편집 중입니다.'
                : 'Editing the English contact description.'}
            </Typography>
          )}
          <Box
            inert={saving ? '' : undefined}
            aria-disabled={saving || undefined}
            sx={{ minWidth: 0, overflowWrap: 'anywhere', '& > div': { minWidth: 0 } }}
          >
            <ReactQuillEditor value={editContent} onChange={(value) => setEditContent(value)} />
          </Box>
          {saveError && (
            <Typography role="alert" color="error">
              {saveError}
            </Typography>
          )}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 1,
              mt: { xs: '30px', lg: '50px' },
            }}
          >
            <BasicButton
              variant="outlined"
              disabled={saving}
              onClick={() => (editorDirty ? setIsCancelDialogOpen(true) : handleCancelConfirm())}
              sx={{ width: { md: '110px' }, height: '48px' }}
            >
              취소
            </BasicButton>
            <BasicButton
              variant="contained"
              color="primary"
              disabled={saving}
              aria-busy={saving}
              onClick={handleDescSubmit}
              sx={{ width: { md: '110px' }, height: '48px' }}
            >
              등록
            </BasicButton>
          </Box>
        </Box>
        <CancelConfirmDialog
          open={isCancelDialogOpen}
          onClose={() => setIsCancelDialogOpen(false)}
          onConfirm={handleCancelConfirm}
        />
      </PageLayout>
    );
  }

  if (success) {
    return (
      <PageLayout>
        {draftGuard}
        <Stack spacing={2.5} alignItems={'center'}>
          <img
            alt="alertCompletedIcon"
            src={alertCompletedIcon}
            sx={{ width: '32px', height: '32px' }}
          />
          <Typography fontSize={'20px'} fontWeight={'bold'}>
            제출이 완료되었습니다!
          </Typography>
        </Stack>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      {draftGuard}
      <SEO
        title="문의"
        description="협업, 광고 등의 문의는 아래 양식을 통해 보내주시면 검토 후 신속하게 회신드리겠습니다."
        url={`${SITE_URL}/contact`}
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Postpop', item: SITE_URL },
              { '@type': 'ListItem', position: 2, name: '문의' },
            ],
          })}
        </script>
      </Helmet>
      <Box
        sx={{
          maxWidth: '780px',
          minWidth: 0,
          width: '100%',
          mx: 'auto',
          '& .MuiFormControl-root': { minWidth: 0 },
        }}
      >
        <Stack>
          {/* 타이틀 */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Typography
              sx={{ fontWeight: 'bold', fontSize: { lg: '28px', md: '22px', xs: '20px' } }}
            >
              문의
            </Typography>
            {isAdmin && (
              <span ref={editTrigger}>
                <IconButton
                  aria-label="toggle edit"
                  onClick={onClickEdit}
                  disabled={descLoading || loadError || isSubmitting}
                  sx={{ width: 44, height: 44, flexShrink: 0 }}
                >
                  <EditIcon />
                </IconButton>
              </span>
            )}
          </Box>
          <Divider sx={{ width: '100%', marginTop: 2 }} />

          {loadError && (
            <Box role="alert">
              <Typography>안내를 불러오지 못했습니다.</Typography>
              <BasicButton onClick={() => setReload((value) => value + 1)}>다시 시도</BasicButton>
            </Box>
          )}
          {/* 설명 */}
          <ProgressBar isLoading={descLoading}>
            <Box sx={{ width: '100%' }} mt={{ lg: 4, md: 2, xs: 1 }} mb={4}>
              <ContentViewer text={descContent} sx={{ color: theme.palette.black[4] }} />
            </Box>
          </ProgressBar>

          {/* 입력폼 */}
          <Box
            component="fieldset"
            disabled={isSubmitting}
            sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}
          >
            <Grid container>
              <Grid item container spacing={'20px'} mb={'20px'}>
                <Grid item xs={12} md={6}>
                  <Stack spacing={1}>
                    <Typography variant="body3" color={theme.palette.black[7]}>
                      성함<span style={{ color: theme.palette.secondary.main }}>*</span>
                    </Typography>
                    <CustomTextField
                      {...register('name')}
                      error={!!errors.name}
                      helperText={errors.name?.message}
                      placeholder="성함을 입력해 주세요."
                      fullWidth
                    />
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Stack spacing={1}>
                    <Typography variant="body3" color={theme.palette.black[7]}>
                      이메일<span style={{ color: theme.palette.secondary.main }}>*</span>
                    </Typography>
                    <CustomTextField
                      {...register('email')}
                      type="email"
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      placeholder="이메일을 입력해 주세요."
                      fullWidth
                    />
                  </Stack>
                </Grid>
              </Grid>

              <Grid item container spacing={'20px'} mb={'20px'}>
                <Grid item xs={12} md={6}>
                  <Stack spacing={1}>
                    <Typography variant="body3" color={theme.palette.black[7]}>
                      연락처
                    </Typography>
                    <CustomTextField {...register('telephone')} fullWidth />
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Stack spacing={1}>
                    <Typography variant="body3" color={theme.palette.black[7]}>
                      국가
                    </Typography>
                    <CustomSelectBox
                      options={countryList}
                      {...register('location')}
                      value={watch('location')}
                      fullWidth
                    />
                  </Stack>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Stack spacing={1}>
                  <Typography variant="body3" color={theme.palette.black[7]}>
                    내용<span style={{ color: theme.palette.secondary.main }}>*</span>
                  </Typography>
                  <CustomTextareaAutosize
                    minRows={6.5}
                    {...register('message')}
                    error={!!errors.message}
                    helperText={errors.message?.message}
                    placeholder="내용을 입력해 주세요."
                    maxLength={5000}
                  />
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Stack>

        {submitError && <Typography role="alert">{submitError}</Typography>}
        {/* 제출 버튼 */}
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
          <Box
            display="flex"
            justifyContent="center"
            mt={{ lg: '50px', md: '40px', xs: '40px' }}
            sx={{ width: { xs: '100%', md: '19%', lg: '19%' } }}
          >
            <BasicButton
              fullWidth
              type="submit"
              disabled={isSubmitting}
              onClick={handleSubmit(onSubmit)}
              sx={{ height: '48px', minWidth: '150px' }}
            >
              제출
            </BasicButton>
          </Box>
        </Box>
      </Box>
    </PageLayout>
  );
};

export default function ContactAdmin() {
  return <AccountBoundary component={Contact} />;
}
